// @flow
import React, { useContext } from "react"
import classnames from "classnames"
import bind from "memoize-bind"
import Translate from "@kiwicom/nitro/lib/components/Translate"
import intl from "@kiwicom/nitro/lib/services/intl/context"
import styled from "styled-components"
import PassengerOutline from "@kiwicom/orbit-components/lib/icons/PassengerOutline"
import FlightReturn from "@kiwicom/orbit-components/lib/icons/FlightReturn"
import FlightMulticity from "@kiwicom/orbit-components/lib/icons/FlightMulticity"
import FlightDirect from "@kiwicom/orbit-components/lib/icons/FlightDirect"

import Timestamp from "common/components/Timestamp/Timestamp"
import { resolveTripPlaceData } from "common/services/starred/utils"
import typeof { goToJourney as GoToJourney } from "common/services/starred/starredDuck"
import { itemIsValid, itemPriceChanged } from "common/records/Starred"
import JourneyStar from "common/components/JourneyActionButtons/JourneyStar"
import type { StarredItem, StarredPlaces } from "common/records/Starred.types"
import type Journey from "common/records/Journey"
import type PassengersCount from "common/records/PassengersCount"
import type { CabinClass } from "common/records/CabinClass"
import Price from "common/components/Price/Price"

type Props = {
  item: StarredItem,
  starredPlaces: StarredPlaces,
  onToggleStar: (Journey, PassengersCount, ?CabinClass) => void,
  goToJourney: GoToJourney,
}

const StarredMonthBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

const StarredMonthNumber = styled.span`
  margin-left: ${({ theme }) => theme.orbit.spaceXXXSmall};
`

const StarredMonthIcon = styled.span`
  position: relative;
  top: -2px;
`

const ITEM_STATUS = {
  INVALID: {
    className: "_expired",
    tKey: __("starred.not_available"),
  },
  PRICE_CHANGED: {
    className: "_pending",
    tKey: __("starred.price_changed"),
  },
  NO_CHANGE: {
    className: "_confirmed",
    tKey: __("starred.no_changes"),
  },
}

export const getRedirectionType = (item: StarredItem) =>
  item.form.starType === "search" ? (
    <Translate t="starred.redirection_type_search" />
  ) : (
    <Translate t="starred.redirection_type_booking" />
  )

const StarredMonthItem = ({ item, starredPlaces, onToggleStar, goToJourney }: Props) => {
  const { translate } = useContext(intl)
  const { journey } = item
  const isReturn = journey.isReturn()
  const isMulticity = journey.isMulticity()

  const departure = journey.getDeparture()
  const departurePlace = resolveTripPlaceData(departure.get("where"), starredPlaces)
  const arrival = journey.getArrival()
  const arrivalPlace = resolveTripPlaceData(arrival.get("where"), starredPlaces)

  const { passengers } = item.form

  let status

  if (!itemIsValid(item)) {
    status = ITEM_STATUS.INVALID
  } else if (itemPriceChanged(item)) {
    status = ITEM_STATUS.PRICE_CHANGED
  } else {
    status = ITEM_STATUS.NO_CHANGE
  }

  let icon

  if (isReturn) {
    icon = <FlightReturn color="secondary" />
  } else if (isMulticity) {
    icon = <FlightMulticity color="secondary" />
  } else {
    icon = <FlightDirect color="secondary" />
  }

  return (
    <a className="StarredMonthItem divTable-tr" onClick={bind(goToJourney, null, item)}>
      <div className="divTable-td StarredMonthItem-route" data-title={translate("common.flight")}>
        <span>
          <span>{departurePlace.get("name")}</span>
          {!departure.getIn(["where", "station"]) && (
            <>
              &nbsp;
              <span>({departurePlace.get("code")})</span>
            </>
          )}
        </span>{" "}
        <StarredMonthIcon>{icon}</StarredMonthIcon>
        <span>
          <span>{arrivalPlace.get("name")}</span>
          {!arrival.getIn(["where", "station"]) && (
            <>
              &nbsp;
              <span>({arrivalPlace.get("code")})</span>
            </>
          )}
        </span>
      </div>

      <div className="divTable-td" data-title={translate("common.price")}>
        <Price>{journey.getBestPrice()}</Price>
      </div>

      <div className="divTable-td" data-title={translate("common.departure_date")}>
        <Timestamp value={departure.getIn(["when", "utc"])} format="${dateFormatPlain}" />
      </div>

      <div className="divTable-td" data-title={translate("common.date_added")}>
        <Timestamp value={item.createdAt} format="${dateFormatPlain}" />
      </div>

      <div className="divTable-td" data-title={translate("common.included")}>
        <StarredMonthBox>
          <PassengerOutline size="small" />
          <StarredMonthNumber>{passengers.getSum()}</StarredMonthNumber>
        </StarredMonthBox>
      </div>

      <div className="divTable-td" data-title={translate("starred.search_or_booking")}>
        <span>{getRedirectionType(item)}</span>
      </div>

      <div className="divTable-td" data-title={translate("common.status")}>
        <span className={classnames("spStatus", status.className)}>
          <Translate t={status.tKey} />
        </span>
      </div>

      <div className="divTable-td" data-title={translate("starred.remove_starred")}>
        <JourneyStar
          active
          journey={journey}
          passengers={passengers}
          cabinClass={item.form.cabinClass}
          onToggle={onToggleStar}
        />
      </div>
    </a>
  )
}

export default StarredMonthItem
